/**
 * <tier-bundle> custom element.
 *
 * Owns the campaign-page tier-bundle interactivity:
 *   - Stage 1: tier-card grid (the customer picks a target qty bracket).
 *   - Stage 2: variant picker with per-variant qty steppers.
 *
 * The "active tier" (and thus the discount %) is computed from TOTAL
 * selected qty, not the tier card the customer originally clicked
 * — auto-promote semantics. The tier-card click in Stage 1 just sets
 * the disabled-CTA template's target ("Избери поне X"), nothing else.
 *
 * On submit, POSTs every selected variant + every visible product-type
 * gift slot to /cart/add.js with line-item-property metadata, then
 * hard-redirects to /checkout. No drawer, no /cart page.
 *
 * Spec: docs/superpowers/specs/2026-05-07-tier-bundle-section-design.md
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('tier-bundle')) return;

  const ADD_URL = '/cart/add.js';

  function makeSessionId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /**
   * Format a price value (in Shopify cents) into the storefront's money format.
   * Falls back to a simple "X.XX €" if Shopify.formatMoney isn't loaded.
   */
  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    const value = (cents / 100).toFixed(2);
    return value + ' €';
  }

  class TierBundle extends HTMLElement {
    constructor() {
      super();
      this.state = {
        stage: 1,
        selectedTierIndex: 1,
        variantQtys: new Map(),
        busy: false,
      };
    }

    connectedCallback() {
      const configEl = this.querySelector('[data-tier-bundle-config]');
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[tier-bundle] config parse error:', e);
        return;
      }

      // Initialize selected tier from config (Liquid already coerced it
      // into [1..4]; defensive parse here in case settings drift).
      const def = parseInt(this.config.defaultTier, 10);
      this.state.selectedTierIndex = def >= 1 && def <= 4 ? def : 1;

      // Seed variantQtys: every variant row known up front, qty 0.
      this.querySelectorAll('[data-variant-row]').forEach((row) => {
        const variantId = parseInt(row.dataset.variantId, 10);
        if (variantId) this.state.variantQtys.set(variantId, 0);
      });

      this.bindEvents();
      this.renderAll();
      this.setupStickyObserver();
    }

    disconnectedCallback() {
      if (this._stickyObserver) {
        this._stickyObserver.disconnect();
        this._stickyObserver = null;
      }
    }

    /**
     * Toggles the sticky CTA's visibility based on the section's position
     * in the viewport. Shows ONLY when the section has been scrolled past
     * (its bottom edge is above the viewport top). Hides when the section
     * is in view, OR below the viewport (i.e., before the user has reached
     * it — no point pre-empting the in-section ATC with a sticky duplicate).
     */
    setupStickyObserver() {
      if (typeof IntersectionObserver === 'undefined') return;
      const stickyEl = this.querySelector('[data-sticky-cta]');
      if (!stickyEl) return;

      this._stickyObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const scrolledPast = entry.boundingClientRect.bottom < 0;
          stickyEl.classList.toggle(
            'is-visible',
            !entry.isIntersecting && scrolledPast,
          );
        },
        { threshold: 0 },
      );
      this._stickyObserver.observe(this);
    }

    /* ---------------- derived values ---------------- */

    /**
     * Total qty in play. Stage 1 derives from the selected tier's qty
     * (so the gift strip can preview "what you'd get"); Stage 2 uses
     * the actual sum across variant rows.
     */
    totalQty() {
      if (this.state.stage === 1) {
        const tier = this.tierAt(this.state.selectedTierIndex);
        return tier ? tier.qty : 0;
      }
      let sum = 0;
      this.state.variantQtys.forEach((q) => { sum += q; });
      return sum;
    }

    /**
     * Active tier under auto-promote rules: highest tier whose qty is
     * ≤ totalQty. Falls back to tier 1 if total is 0 (so price displays
     * something sensible at idle).
     */
    activeTier() {
      const total = this.totalQty();
      const tiers = (this.config.tiers || []).slice().sort((a, b) => a.qty - b.qty);
      let active = tiers[0];
      for (const t of tiers) {
        if (total >= t.qty) active = t;
      }
      return active || { index: 1, qty: 1, pct: 0, label: '' };
    }

    tierAt(index) {
      return (this.config.tiers || []).find((t) => t.index === index);
    }

    discountPct() {
      return this.activeTier().pct || 0;
    }

    /** Per-bottle price under the active tier, in cents. */
    unitPrice() {
      const base = this.config.basePrice || 0;
      const pct = this.discountPct();
      // Math here mirrors the Liquid-side calculation so tier card
      // prices and the live ATC total agree byte-for-byte.
      return Math.round((base * (100 - pct)) / 100);
    }

    totalPrice() {
      return this.unitPrice() * this.totalQty();
    }

    totalCompare() {
      return (this.config.basePrice || 0) * this.totalQty();
    }

    /** For Stage 2 enable: the customer must hit their CHOSEN tier minimum. */
    targetQty() {
      const tier = this.tierAt(this.state.selectedTierIndex);
      return tier ? tier.qty : 1;
    }

    canSubmit() {
      if (this.state.busy) return false;
      if (this.state.stage !== 2) return false;
      return this.totalQty() >= this.targetQty();
    }

    /* ---------------- events ---------------- */

    bindEvents() {
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-tier-radio]') && t.checked) {
        this.state.selectedTierIndex = parseInt(t.value, 10);
        this.renderActiveTierCard();
        this.renderGiftStrip();
        this.renderCta();
      }
    }

    onClick(e) {
      // ATC button — Stage 1 advance, Stage 2 submit.
      const cta = e.target.closest('[data-tier-bundle-cta]');
      if (cta && this.contains(cta)) {
        e.preventDefault();
        if (this.state.stage === 1) {
          this.setStage(2);
        } else {
          this.submitOrder();
        }
        return;
      }

      // Back to tiers from Stage 2.
      if (e.target.closest('[data-back-to-tiers]')) {
        this.setStage(1);
        return;
      }

      // Qty stepper buttons.
      const dec = e.target.closest('[data-qty-decrement]');
      if (dec) {
        const row = dec.closest('[data-variant-row]');
        if (row) this.changeQty(row, -1);
        return;
      }
      const inc = e.target.closest('[data-qty-increment]');
      if (inc && !inc.disabled) {
        const row = inc.closest('[data-variant-row]');
        if (row) this.changeQty(row, +1);
        return;
      }

      // Gallery thumb / next / prev.
      const thumb = e.target.closest('[data-thumb]');
      if (thumb) {
        this.swapHero(thumb);
        return;
      }
      if (e.target.closest('[data-gallery-next]')) {
        this.advanceGallery(1);
        return;
      }
      if (e.target.closest('[data-gallery-prev]')) {
        this.advanceGallery(-1);
        return;
      }

      // Sticky CTA — its own click semantics. When the main CTA would be
      // disabled (Stage 1 OR Stage 2 with insufficient qty) the sticky
      // scrolls the page back to the section so the user can pick. When
      // the main CTA would submit, the sticky submits too.
      const stickyCta = e.target.closest('[data-tier-bundle-sticky-cta]');
      if (stickyCta && this.contains(stickyCta)) {
        e.preventDefault();
        if (this.canSubmit()) {
          this.submitOrder();
        } else {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }
    }

    changeQty(row, delta) {
      const variantId = parseInt(row.dataset.variantId, 10);
      if (!variantId) return;
      const available = row.dataset.variantAvailable === 'true';
      if (!available && delta > 0) return; // defensive — `+` is also disabled
      const current = this.state.variantQtys.get(variantId) || 0;
      const next = Math.max(0, current + delta);
      if (next === current) return;
      this.state.variantQtys.set(variantId, next);
      this.renderVariantRow(row);
      this.renderGiftStrip();
      this.renderCurrentTierInfo();
      this.renderCta();
    }

    setStage(n) {
      const previous = this.state.stage;
      this.state.stage = n;
      this.clearError();
      this.renderStage();
      this.renderActiveTierCard();
      this.renderGiftStrip();
      this.renderCurrentTierInfo();
      this.renderCta();

      // On Stage 1 -> Stage 2 transition, scroll the variant picker into
      // view. Otherwise on mobile the customer clicks "Избери аромат" and
      // the picker appears below the fold — they have to manually scroll
      // to even see what they're supposed to do next.
      //
      // We wait one animation frame so the layout has actually swapped
      // (Stage 2's `[data-stage-2]` container just had its `hidden`
      // attribute removed) before the browser measures its position.
      // We don't scroll on the reverse direction (back to tiers) — that
      // can disorient and the user almost certainly wants to stay where
      // they were on the page.
      if (n === 2 && previous !== 2) {
        const stage2 = this.querySelector('[data-stage-2]');
        if (stage2) {
          requestAnimationFrame(() => {
            stage2.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      }
    }

    /* ---------------- gallery ---------------- */

    swapHero(thumbBtn) {
      const hero = this.querySelector('[data-tier-bundle-hero]');
      if (!hero) return;
      const src = thumbBtn.dataset.thumbSrc;
      const srcset = thumbBtn.dataset.thumbSrcset;
      if (src) hero.src = src;
      if (srcset) hero.srcset = srcset;

      this.querySelectorAll('[data-thumb]').forEach((b) => {
        b.classList.toggle('is-active', b === thumbBtn);
      });

      // Active thumb just moved — recompute which arrows are reachable.
      this.renderGalleryArrows();
    }

    /**
     * Hide prev/next arrows when there's nowhere to scroll: prev at index 0,
     * next at the last index. Keeps the carousel UX clear at the bounds —
     * no "click and nothing happens" affordance.
     */
    renderGalleryArrows() {
      const thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      if (thumbs.length <= 1) return;
      const activeIdx = thumbs.findIndex((b) => b.classList.contains('is-active'));
      const safeActive = activeIdx === -1 ? 0 : activeIdx;
      const prevBtn = this.querySelector('[data-gallery-prev]');
      const nextBtn = this.querySelector('[data-gallery-next]');
      if (prevBtn) prevBtn.hidden = safeActive <= 0;
      if (nextBtn) nextBtn.hidden = safeActive >= thumbs.length - 1;
    }

    advanceGallery(direction) {
      const step = direction === -1 ? -1 : 1;
      const thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      if (thumbs.length <= 1) return;
      const activeIdx = thumbs.findIndex((b) => b.classList.contains('is-active'));
      const safeActive = activeIdx === -1 ? 0 : activeIdx;
      const nextIdx = safeActive + step;
      // Clamp — no wrap. The arrows are hidden at the bounds via
      // renderGalleryArrows(), so this is defensive (keyboard nav, etc.).
      if (nextIdx < 0 || nextIdx >= thumbs.length) return;
      this.swapHero(thumbs[nextIdx]);
    }

    /* ---------------- render ---------------- */

    renderAll() {
      this.renderStage();
      this.renderActiveTierCard();
      this.renderGiftStrip();
      this.querySelectorAll('[data-variant-row]').forEach((row) => this.renderVariantRow(row));
      this.renderCurrentTierInfo();
      this.renderCta();
      this.renderGalleryArrows();
    }

    renderStage() {
      this.dataset.stage = String(this.state.stage);
      const s1 = this.querySelector('[data-stage-1]');
      const s2 = this.querySelector('[data-stage-2]');
      if (s1) s1.hidden = this.state.stage !== 1;
      if (s2) s2.hidden = this.state.stage !== 2;
    }

    renderActiveTierCard() {
      const cards = this.querySelectorAll('[data-tier-card]');
      cards.forEach((c) => {
        const i = parseInt(c.dataset.tierIndex, 10);
        c.classList.toggle('is-active', i === this.state.selectedTierIndex);
      });
    }

    renderGiftStrip() {
      // Slots stay visible at all times; we only toggle the `is-locked`
      // class. Locked slots show a desaturated/dimmed image with a
      // padlock overlay so the customer sees what they'd unlock by
      // climbing tiers — turns the gift strip into an upsell prompt
      // rather than hidden surprise.
      const total = this.totalQty();
      const slots = this.querySelectorAll('[data-gift-slot]');
      slots.forEach((slot) => {
        const idx = parseInt(slot.dataset.giftSlotIndex, 10);
        slot.classList.toggle('is-locked', idx > total);
      });
    }

    renderVariantRow(row) {
      const variantId = parseInt(row.dataset.variantId, 10);
      const qty = this.state.variantQtys.get(variantId) || 0;
      const valueEl = row.querySelector('[data-qty-value]');
      if (valueEl) valueEl.textContent = String(qty);
      // Visual state: dim row at qty 0 vs highlight at qty > 0.
      row.classList.toggle('is-picked', qty > 0);
      // Decrement button disables at qty 0.
      const decBtn = row.querySelector('[data-qty-decrement]');
      if (decBtn) decBtn.disabled = qty === 0;
    }

    renderCurrentTierInfo() {
      const el = this.querySelector('[data-current-tier-info]');
      if (!el) return;
      const total = this.totalQty();
      if (this.state.stage !== 2 || total === 0) {
        el.hidden = true;
        el.textContent = '';
        return;
      }
      const tier = this.activeTier();
      const fmt = this.config.currencyFormat;
      const price = formatMoney(this.unitPrice(), fmt);
      // Prefer the tier label (with merchant pluralization) if present.
      // Otherwise fall back to a numeric readout.
      const label = tier.label && tier.label.length ? tier.label : String(total);
      let text = total + ' × ' + label + ' — ' + price + '/бр.';
      if (tier.pct > 0) text += ' (-' + tier.pct + '%)';
      el.textContent = text;
      el.hidden = false;
    }

    renderCta() {
      // Update both the main ATC and the sticky CTA in one pass. They share
      // the same `data-cta-*` inner attributes so label and prices stay in
      // sync; only the disabled state differs (sticky stays clickable so
      // its "scroll back to section" click always works).
      const btns = this.querySelectorAll(
        '[data-tier-bundle-cta], [data-tier-bundle-sticky-cta]',
      );
      btns.forEach((btn) => this._renderCtaButton(btn));
    }

    _renderCtaButton(btn) {
      const isSticky = btn.matches('[data-tier-bundle-sticky-cta]');
      // There are TWO `[data-cta-label]` spans (desktop + mobile) for the
      // theme's standard responsive button-text styling — keep them in sync.
      const labelEls = btn.querySelectorAll('[data-cta-label]');
      const setLabel = (text) => labelEls.forEach((el) => { el.textContent = text; });
      const pricesEl = btn.querySelector('[data-cta-prices]');
      const compareEl = btn.querySelector('[data-cta-compare]');
      const totalEl = btn.querySelector('[data-cta-total]');
      const fmt = this.config.currencyFormat;
      const labels = this.config.labels || {};

      btn.setAttribute('aria-busy', this.state.busy ? 'true' : 'false');

      if (this.state.stage === 1) {
        setLabel(labels.stage1Cta || '');
        if (pricesEl) pricesEl.hidden = true;
        btn.disabled = false;
        return;
      }

      // Stage 2.
      const total = this.totalQty();
      const target = this.targetQty();

      if (total < target) {
        // Disabled state — show "select at least N" with interpolation.
        const tmpl = labels.stage2CtaDisabledTemplate || 'Select at least {qty}';
        setLabel(tmpl.replace('{qty}', String(target)));
        if (pricesEl) pricesEl.hidden = true;
        // Sticky stays clickable so its scroll-back action always works;
        // main CTA is disabled so the user can't prematurely submit.
        btn.disabled = !isSticky;
        return;
      }

      // Enabled state — show enabled label + prices.
      setLabel(labels.stage2CtaEnabled || '');
      if (compareEl) {
        const cmp = this.totalCompare();
        const tot = this.totalPrice();
        // Hide strikethrough when there's no actual discount.
        if (cmp > tot) {
          compareEl.textContent = formatMoney(cmp, fmt);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }
      if (totalEl) totalEl.textContent = formatMoney(this.totalPrice(), fmt);
      if (pricesEl) pricesEl.hidden = false;
      btn.disabled = this.state.busy;
    }

    /* ---------------- cart submission ---------------- */

    /**
     * Build the items[] payload for /cart/add.js. Includes:
     *   - Every campaign variant with qty > 0, with `_deal_discount_pct`
     *     stamped (read by the deal-line-discount Function) and a campaign
     *     session ID for support / reporting.
     *   - Every VISIBLE product-type gift slot, qty 1, at €0, linked to
     *     the campaign session via `_gift_for` (mirroring deal-selector
     *     so order data shape stays consistent across modules).
     *   - Decorative slots are NOT line items — they ride on the merchant's
     *     existing free-shipping threshold or are pure visual decoration.
     */
    buildCartPayload(sessionId) {
      const items = [];
      const pct = this.discountPct();
      const total = this.totalQty();

      // Campaign variant lines.
      //
      // `_deal: 'b'` is REQUIRED — the deal-line-discount Shopify Function
      // gates on `_deal == 'b'` before reading `_deal_discount_pct`, so
      // omitting it makes the Function skip the line and the discount
      // never applies at checkout. (Same property the deal-selector PDP
      // block uses for its Deal B path; we ride on the same gate so we
      // don't need to deploy a new Function variant.)
      this.state.variantQtys.forEach((qty, variantId) => {
        if (qty > 0) {
          items.push({
            id: variantId,
            quantity: qty,
            properties: {
              _deal: 'b',
              _deal_discount_pct: String(pct),
              _campaign_session: sessionId,
            },
          });
        }
      });

      // Gift slot lines (only product-type, only those visible at current qty).
      (this.config.giftSlots || []).forEach((slot) => {
        if (slot.type !== 'product') return;
        if (slot.index > total) return;
        if (!slot.variantId || slot.available === false) return;
        items.push({
          id: slot.variantId,
          quantity: 1,
          properties: {
            _gift_for: sessionId,
            _free_gift: 'Yes',
          },
        });
      });

      return items;
    }

    async submitOrder() {
      if (!this.canSubmit()) return;
      this.state.busy = true;
      this.renderCta();
      this.clearError();

      const sessionId = makeSessionId();
      const items = this.buildCartPayload(sessionId);

      if (items.length === 0) {
        // Safety net — canSubmit() should already prevent this.
        this.state.busy = false;
        this.renderCta();
        return;
      }

      try {
        const res = await fetch(ADD_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const errPayload = await res.json().catch(() => ({}));
          throw new Error(errPayload.description || errPayload.message || 'Add to cart failed');
        }
        // Drain the response body so the connection can release before we navigate.
        await res.json().catch(() => ({}));

        // Success — straight to checkout. No drawer, no cart page.
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[tier-bundle] submitOrder error:', err);
        this.showError(err && err.message ? err.message : 'Could not place order');
        this.state.busy = false;
        this.renderCta();
      }
    }

    showError(msg) {
      const el = this.querySelector('[data-tier-bundle-error]');
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
    }

    clearError() {
      const el = this.querySelector('[data-tier-bundle-error]');
      if (!el) return;
      el.textContent = '';
      el.hidden = true;
    }
  }

  customElements.define('tier-bundle', TierBundle);
})();
